/**
 * モデルの並び（前から順に試す）を編集する
 *
 * 並べ替え・削除ができ、追加は Gemini のモデル一覧から選ぶ（文字は打たせない）。
 * 一覧にないモデルが入っているときは警告を出す（廃止された可能性がある）。
 */

import { ArrowDown, ArrowUp, TriangleAlert, X } from 'lucide-react'

import type { AiModelOption, AiModelsResponse } from '@/api/types/ai-usages'
import { Button } from '@/components/ui/button'

type Props = {
  models: string[]
  onChange: (models: string[]) => void
  /** null は読み込み中 */
  catalog: AiModelsResponse | null
  /** これより少なくは削れない */
  minCount: number
}

const moved = (list: string[], from: number, to: number): string[] => {
  const item = list.at(from)
  if (item === undefined || to < 0 || to >= list.length) return list
  const rest = list.filter((_, index) => index !== from)
  return [...rest.slice(0, to), item, ...rest.slice(to)]
}

const quotaText = (option: AiModelOption | undefined): string => {
  if (!option) return ''
  if (option.daily_limit === null) return '無料枠は未確認（毎分 5 回で数え、上限に当たったら覚える）'
  const learned = option.quota_source === 'learned' ? '（上限に当たった時点の回数）' : ''
  return `無料 ${option.daily_limit}回/日${learned}・${option.per_minute_limit ?? '?'}回/分`
}

const optionLabel = (option: AiModelOption): string => {
  const quota = option.daily_limit === null ? '枠未確認' : `無料 ${option.daily_limit}/日`
  return `${option.name}（${option.display_name}）・${quota}`
}

export const AiModelListEditor = ({ models, onChange, catalog, minCount }: Props) => {
  const options = catalog?.models ?? []
  const byName = new Map(options.map((option) => [option.name, option]))
  const addable = options.filter((option) => !models.includes(option.name))

  return (
    <div className="space-y-1">
      <ol className="space-y-1">
        {models.map((model, index) => {
          const option = byName.get(model)
          const missing = catalog?.source === 'api' && !option
          return (
            <li key={model} className="rounded-md border bg-background px-2 py-1">
              <div className="flex items-center gap-1">
                <span className="w-5 text-xs text-muted-foreground">{index + 1}.</span>
                <span className="flex-1 truncate font-mono text-sm">{model}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`${model} を上へ`}
                  disabled={index === 0}
                  onClick={() => onChange(moved(models, index, index - 1))}
                >
                  <ArrowUp />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`${model} を下へ`}
                  disabled={index === models.length - 1}
                  onClick={() => onChange(moved(models, index, index + 1))}
                >
                  <ArrowDown />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`${model} を外す`}
                  disabled={models.length <= minCount}
                  onClick={() => onChange(models.filter((m) => m !== model))}
                >
                  <X />
                </Button>
              </div>
              {missing ? (
                <p className="flex items-center gap-1 pl-6 text-xs text-amber-600 dark:text-amber-400">
                  <TriangleAlert className="size-3" />
                  Gemini の一覧にありません（廃止された可能性があります）
                </p>
              ) : (
                <p className="pl-6 text-xs text-muted-foreground">
                  {option?.display_name} {quotaText(option)}
                </p>
              )}
            </li>
          )
        })}
      </ol>

      <select
        aria-label="モデルを選んで追加"
        value=""
        disabled={!catalog || addable.length === 0}
        className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
        onChange={(e) => {
          if (e.target.value) onChange([...models, e.target.value])
        }}
      >
        <option value="">
          {!catalog
            ? 'モデル一覧を読み込み中…'
            : addable.length === 0
              ? '追加できるモデルはありません'
              : 'モデルを選んで追加…'}
        </option>
        {addable.map((option) => (
          <option key={option.name} value={option.name}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
      {catalog?.source === 'fallback' && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Gemini のモデル一覧を取れなかったため、コードに載っているモデルだけを出しています。
        </p>
      )}
    </div>
  )
}
